/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { FormSelectField, FormTextField, FormTextareaField } from 'components/common/form/Form';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import {
  listQuoteConversations,
  listQuoteOrganizations,
  purchaseEmail,
  resetImportedAttachments,
  sendQuotePurchaseOrder,
  updateQuotePurchaseSettings,
} from 'store/slices/marketplace';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';
import { RecipientField } from 'modules/marketplace/RecipientField';
import { sendPurchaseOrderSchema } from 'validation/form/marketplace';
import { QuoteAttachment } from 'modules/marketplace/quote/QuoteAttachment';
import { Typography } from '@mui/material';

export const SendPurchaseOrder = (props) => {
  const { close } = props;
  const dispatch = useDispatch();
  const dispatchAction = useAsyncDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const loading = useSelector((state) => state.marketplace.sendPurchaseOrderForm.loading);
  const selectedQuoteSet = useSelector((state) => state.marketplace.selectedQuoteSet);
  const selectedQuoteProduct = useSelector((state) => state.marketplace.selectedQuoteProduct.value);
  const sendTogether = useSelector((state) => state.marketplace.selectedQuoteProduct.sendTogether);
  const purchaseEmailData = useSelector((state) => state.marketplace.purchaseEmailData);
  const importedAttachments = useSelector((state) => state.marketplace.importedAttachments);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const quoteOrganizationList = useSelector((state) => state.marketplace.quoteOrganizations.value);

  const totalUploadedFilesSize = useMemo(() => {
    return importedAttachments.reduce((total, file) => {
      return (total += file?.size);
    }, 0);
  }, [importedAttachments]);
  const hasExceedMaxAttachmentSize = useMemo(() => totalUploadedFilesSize > 10000000, [totalUploadedFilesSize]);

  const methods = useForm({
    resolver: yupResolver(sendPurchaseOrderSchema),
    defaultValues: {
      subject: '',
      body: '',
      to: [],
      cc: [],
      bcc: [],
      reply_to: [],
      data: {
        organisation_id: '',
      },
      note: '',
    },
  });

  // handle save
  const handleSave = methods.handleSubmit((data) => {
    // prepare email data
    const attachments = importedAttachments.filter((attachment) => attachment?.id).map((attachment) => attachment.id);
    const emailData = {
      ...data,
      data: {
        ...data.data,
        organisation_id: data?.data?.organisation_id || '',
        organisation_name: quoteOrganizationList.find((org) => org.id === data?.data?.organisation_id)?.name || '',
      },
      attachments,
      cc: data?.cc && data.cc.length > 0 ? data.cc : null,
      bcc: data?.bcc && data.bcc.length > 0 ? data.bcc : null,
      reply_to: data?.reply_to && data.reply_to.length > 0 ? data.reply_to : null,
    };

    // update quote purchase settings if organisation is changed
    if (data?.data?.organisation_id && purchaseEmailData?.data?.organisation_id != data?.data?.organisation_id) {
      dispatch(
        updateQuotePurchaseSettings({
          quoteId: selectedQuoteProduct?.quote_id,
          formData: {
            selected_organisation_id: data?.data?.organisation_id || null,
          },
        })
      );
    }

    // send purchase order email
    dispatchAction(sendQuotePurchaseOrder({ emailData, quoteId: selectedQuoteProduct?.quote_id }), {
      onSuccess: () => {
        enqueueSnackbar('Purchase Order has been sent');
        dispatch(listQuoteConversations({ quoteId: selectedQuoteProduct?.quote_id }));
        close();
      },
    });
  });

  // prepare email data
  const handlePurchaseEmailData = (purchaseEmailData, sendTogether, selectedQuoteProduct, selectedQuoteSet) => {
    if (!purchaseEmailData) return {};

    if (sendTogether) {
      const { subject = '', description = '', to = [], data = {} } = purchaseEmailData;
      const name = selectedQuoteSet?.name || '';
      return {
        subject: `${subject} ${name}`.trim(),
        body: description,
        to,
        reply_to: currentUser?.email ? [currentUser.email] : [],
        data,
      };
    }

    const { product_name = '', description = '', price = 0 } = selectedQuoteProduct || {};
    const name = selectedQuoteSet?.name || '';
    return {
      ...purchaseEmailData,
      subject: `${purchaseEmailData.subject || ''} ${name}`.trim(),
      body: `${product_name} - ${description} - $${Number(price).toFixed(2)}`,
      reply_to: currentUser?.email ? [currentUser.email] : [],
    };
  };

  // reset imported attachments on unmount
  useEffect(() => {
    dispatch(resetImportedAttachments());
  }, [dispatch]);

  // fetch purchase email template
  useEffect(() => {
    if (selectedQuoteSet?.id && selectedQuoteProduct?.id) {
      dispatch(
        purchaseEmail({
          quoteId: selectedQuoteProduct?.quote_id,
          supplierId: selectedQuoteProduct?.supplier_id,
          itemId: selectedQuoteProduct?.id,
          orgId: selectedQuoteProduct?.quote_org_id,
          sendTogether,
        })
      );
    }
  }, [dispatch, selectedQuoteSet, selectedQuoteProduct, sendTogether]);

  // fetch quote organization list
  useEffect(() => {
    if (selectedQuoteProduct?.quote_id) {
      dispatch(listQuoteOrganizations({ quoteId: selectedQuoteProduct?.quote_id }));
    }
  }, [dispatch, selectedQuoteProduct]);

  // set form data when purchase email data is fetched
  useEffect(() => {
    if (purchaseEmailData?.from) {
      const data = handlePurchaseEmailData(purchaseEmailData, sendTogether, selectedQuoteProduct, selectedQuoteSet);
      methods.reset(data);
    }
  }, [purchaseEmailData, sendTogether, methods, selectedQuoteProduct, selectedQuoteSet]);

  return (
    <DrawerContent>
      <DrawerContent.Header title="Send Purchase Order" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <FormProvider {...methods}>
            {purchaseEmailData?.from && (
              <>
                <RecipientField
                  id="to"
                  name="to"
                  label="To"
                  options={[]}
                  control={methods.control}
                  hasError={!!methods.formState.errors.to}
                  error={methods.formState.errors.to?.message}
                />
                <RecipientField
                  id="cc"
                  name="cc"
                  label="CC"
                  options={[]}
                  control={methods.control}
                  hasError={!!methods.formState.errors.cc}
                  error={methods.formState.errors.cc?.message}
                />
                <RecipientField
                  id="bcc"
                  name="bcc"
                  label="BCC"
                  options={[]}
                  control={methods.control}
                  hasError={!!methods.formState.errors.bcc}
                  error={methods.formState.errors.bcc?.message}
                />
                <RecipientField
                  id="reply_to"
                  name="reply_to"
                  label="Reply To"
                  options={[]}
                  control={methods.control}
                  hasError={!!methods.formState.errors.reply_to}
                  error={methods.formState.errors.reply_to?.message}
                />
                <FormSelectField
                  name="data.organisation_id"
                  label="Organisation"
                  control={methods.control}
                  options={quoteOrganizationList || []}
                  valueName="id"
                  optionName="name"
                />
                <FormTextField
                  name="subject"
                  label="Subject"
                  control={methods.control}
                />
                <FormTextareaField
                  name="body"
                  label="Description"
                  control={methods.control}
                  rows={5}
                  disabled={true}
                />
                <FormTextareaField
                  name="note"
                  label="Note"
                  control={methods.control}
                />
                <QuoteAttachment
                  quoteId={selectedQuoteProduct?.quote_id}
                  itemId={selectedQuoteProduct?.id}
                />
                {hasExceedMaxAttachmentSize && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ fontWeight: 400 }}
                  >
                    Attachment size exceeds 10 MB limit
                  </Typography>
                )}
              </>
            )}
          </FormProvider>
        </DrawerContent.Content>
        <DrawerContent.ActionButtons
          submitButtonText="Send"
          submitButtonLoading={loading}
          disabled={hasExceedMaxAttachmentSize}
          submit={handleSave}
          close={close}
        />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
